import { React, ReactRouter, useAPI } from 'mcelroy-lib'
import { FormPage, useUserContext, useToast } from 'mcelroy-components'

/*
    Distributor page
*/
export function DistributorPage() {
    const routeParams = ReactRouter.useParams()
    const distributorId = routeParams.distributor_id

    if (distributorId != null)
        return <UpdateDistributorPage distributorId={distributorId} />
    else
        return <CreateDistributorPage />
}

/*
    Distributor page (Update existing)
*/
function UpdateDistributorPage({ distributorId }) {
    const [isEditMode, setIsEditMode] = React.useState(false)

    // API distributor info query
    const { loading, data, error, rerun: rerunLoad } = useAPI(`
        query($id: Int!){
            distributorLocation(id: $id){
                id customerNbr distributorName
                address address2 address3 city state zip regionName country
                phone phone2 fax
                mainContact email
                salesEmail salesEmail2 salesEmail3
                rentalEmail rentalEmail2 rentalEmail3
                website
                authorizedServiceCenter masterMechanic ppr
                services{
                    location industrial gas service rental certifiedRental ppr 
                }
            }
        }
    `, { id: distributorId })

    if (loading)
        return null
    else if (error)
        return <div>Not authorized</div>
    else
        return <DistributorFormPage distributorId={distributorId} data={data} rerunLoad={rerunLoad} isEditMode={isEditMode} setIsEditMode={setIsEditMode} />
}

/*
    Distributor page (Create new)
*/
function CreateDistributorPage() {
    return <DistributorFormPage isEditMode={true} />
}

/*
    Distributor form page
*/
function DistributorFormPage({ distributorId, data, rerunLoad, isEditMode, setIsEditMode }) {
    const pprBrands = ['aquatherm', 'nupi', 'pestan', 'polymelt', 'wavin', 'uponor']
    const toast = useToast()
    const navigate = ReactRouter.useNavigate()
    const { user: currentUser } = useUserContext()
    const servicesRef = React.useRef(null)

    // API create distributor location mutation
    const { run: createDistributorLocation } = useAPI(`
        mutation($distributor: DistributorInput!){
            createDistributorLocation( distributor:$distributor ){id}
        }
    `)

    // API update distributor location mutation
    const { run: updateDistributorLocation } = useAPI(`
        mutation($distributor: DistributorInput!){
            updateDistributorLocation( distributor:$distributor ){id}
        }
    `)

    // API delete distributor location mutation
    const { run: deleteDistributorLocation } = useAPI(`
        mutation($id: Int!){
            deleteDistributorLocation( id:$id)
        }
    `)

    const asc = data?.distributorLocation.authorizedServiceCenter === 'ASC'
    const mm = data?.distributorLocation.masterMechanic === 'X'
    const ppr = data?.distributorLocation.ppr ?? ''
    const isEditSerivice = isEditMode && currentUser.isEmployee

    const gasAreas = []
    const industrialAreas = []
    const serviceAreas = []
    const rentalAreas = []
    const certifiedRentalAreas = []
    const pprAreas = []
    if (data?.distributorLocation?.services != null) {
        for (const s of data.distributorLocation.services) {
            const locationParts = s.location.split('_')
            if (s.gas) gasAreas.push(locationParts[1])
            if (s.industrial) industrialAreas.push(locationParts[1])
            if (s.service) serviceAreas.push(locationParts[1])
            if(s.rental) rentalAreas.push(locationParts[1])
            if(s.certifiedRental) certifiedRentalAreas.push(locationParts[1])
            if(s.ppr) pprAreas.push(locationParts[1])
        }
    }

    // Form fields
    const fields = [
        {
            label: 'Location',
            fields: [
                { label: 'Distributor Name', disabled: !isEditMode, name: 'distributorName', value: data?.distributorLocation?.distributorName },
                {
                    label: 'Account Number', disabled: !isEditMode, name: 'customerNbr', value: data?.distributorLocation?.customerNbr,
                    validator: (v) => { return Number.isInteger(+v) ? null : 'Must be integer value' },
                    show: currentUser.isEmployee
                },
                {
                    label: 'Account Number', disabled: !isEditMode, name: 'customerNbr', value: data?.distributorLocation?.customerNbr,
                    options: currentUser.accounts.map((aId) => {return {label: aId, value: aId}}),
                    show: currentUser.isDistributor
                },
                {label: 'Address 1', disabled: !isEditMode, name: 'address', value: data?.distributorLocation?.address},
                {label: 'Address 2', disabled: !isEditMode, name: 'address2', value: data?.distributorLocation?.address2},
                {label: 'Address 3', disabled: !isEditMode, name: 'address3', value: data?.distributorLocation?.address3},
                {label: 'City', disabled: !isEditMode, name: 'city', value: data?.distributorLocation?.city},
                {label: 'State', disabled: !isEditMode, name: 'state', value: data?.distributorLocation?.state},
                {label: 'Zip', disabled: !isEditMode, name: 'zip', value: data?.distributorLocation?.zip},
                {label: 'Region', disabled: !isEditMode, name: 'regionName', value: data?.distributorLocation?.regionName},
                {label: 'Country', disabled: !isEditMode, name: 'country', value: data?.distributorLocation?.country},
                {label: 'Phone', disabled: !isEditMode, name: 'phone', value: data?.distributorLocation?.phone},
                {label: 'Phone 2', disabled: !isEditMode, name: 'phone2', value: data?.distributorLocation?.phone2},
                {label: 'Fax', disabled: !isEditMode, name: 'fax', value: data?.distributorLocation?.fax}
            ]
        },
        {
            label: 'Contacts',
            fields: [
                {label: 'Main Contact Name', disabled: !isEditMode, name: 'mainContact', value: data?.distributorLocation?.mainContact},
                {label: 'Main Contact Email', disabled: !isEditMode, name: 'email', value: data?.distributorLocation?.email},
                {label: 'Sales Email', disabled: !isEditMode, name: 'salesEmail', value: data?.distributorLocation?.salesEmail},
                {label: 'Additional Sales Email', disabled: !isEditMode, name: 'salesEmail2', value: data?.distributorLocation?.salesEmail2},
                {label: 'Additional Sales Email', disabled: !isEditMode, name: 'salesEmail3', value: data?.distributorLocation?.salesEmail3},
                {label: 'Certified Rental Email', disabled: !isEditMode, name: 'rentalEmail', value: data?.distributorLocation?.rentalEmail},
                {label: 'Additional Rental Email', disabled: !isEditMode, name: 'rentalEmail2', value: data?.distributorLocation?.rentalEmail2},
                {label: 'Additional Rental Email', disabled: !isEditMode, name: 'rentalEmail3', value: data?.distributorLocation?.rentalEmail3},
                {label: 'Website', disabled: !isEditMode, name: 'website', value: data?.distributorLocation?.website}
            ]
        },
        {
            fields: [
                {
                    label: 'Certifications',
                    fields: [
                        {label: 'Authorized Service Center', disabled: !isEditSerivice, name: 'asc', value: asc, type: 'checkbox'},
                        {label: 'Master Mechanic', disabled: !isEditSerivice, name: 'mm', value: mm, type: 'checkbox'}
                    ]
                },
                {
                    label: 'Polypropylene Support',
                    fields: pprBrands.map(function (b) {
                        return (
                            { label: b, disabled: !isEditSerivice, name: b, value: ppr.includes(b), type: 'checkbox' }
                        )
                    })
                },
                {
                    label: 'Service Areas',
                    fields: [
                        { label: 'Gas', disabled: true, name: 'gasAreas', value: gasAreas.join() },
                        { label: 'Industrial', disabled: true, name: 'industrialAreas', value: industrialAreas.join() },
                        { label: 'Service', disabled: true, name: 'serviceAreas', value: serviceAreas.join() },
                        { label: 'Rental', disabled: true, name: 'rentalAreas', value: rentalAreas.join() },
                        { label: 'Certified Rental', disabled: true, name: 'certifiedRentalAreas', value: certifiedRentalAreas.join() },
                        { label: 'PPR', disabled: true, name: 'pprAreas', value: pprAreas.join() },

                    ]
                }
            ]
        }
    ]

    // Form buttons
    const buttons = []
    createButton(buttons, isEditMode, 'Save', PRIMARY, async (formData) => {
        try {
            const distributor = { ...data?.distributorLocation, ...formData }
            if (formData.asc)
                distributor.authorizedServiceCenter = 'ASC'
            else
                distributor.authorizedServiceCenter = ''
            if (formData.mm)
                distributor.masterMechanic = 'X'
            else
                distributor.masterMechanic = ''

            // add ppr brands
            const ppr = []
            for (const t of pprBrands) {
                if(formData[t])
                    ppr.push(t)
            }
            distributor.ppr = ppr.join(' ')

            // add location services
            const inputs = servicesRef.current.getElementsByTagName('input')
            const servicesMap = {}
            for (const i of inputs) {
                const nameParts = i.name.split('_')
                const location = nameParts[0] + '_' + nameParts[1]
                const locationServices = servicesMap[location] ?? {}
                locationServices.location = location
                locationServices[nameParts[2]] = i.checked
                servicesMap[location] = locationServices
            }
            distributor.services = Object.values(servicesMap)

            if (distributorId != null) {
                await updateDistributorLocation({ distributor: distributor })
                setIsEditMode(false)
                await rerunLoad()
            } else {
                const result = await createDistributorLocation({ distributor: distributor })
                navigate('/distributors/' + result.createDistributorLocation.id)
            }
            toast.success('Distributor saved')
        } catch (err) {
            if (err)
                toast.error(err[0]?.message ?? 'Error updating distributor.')
            else
                toast.error('Error saving distributor')
            console.log(err)
        }
    })
    createButton(buttons, isEditMode, 'Cancel', PLAIN, async () => {
        if (distributorId != null) {
            setIsEditMode(false)
            await rerunLoad()
        } else {
            navigate('/distributors')
        }
    })
    createButton(buttons, distributorId != null && currentUser.isUserAdmin && !isEditMode, 'Edit', PLAIN, () => { setIsEditMode(true) })
    createButton(buttons, distributorId != null && isEditMode, 'Delete', CAUTION, async () => {
        if (confirm('This can not be undone.  Delete distributor?')) {
            await deleteDistributorLocation({ id: data?.distributorLocation?.id })
            toast.success('Distributor deleted.')
            navigate('/distributors')
        }
    })


    // Page display
    return (
        <FormPage title="Distributor" size="lg" fields={fields} buttons={buttons}>
            <div ref={servicesRef}>
                <Certifications distributorLocation={data?.distributorLocation} currentUser={currentUser} isEditMode={isEditMode} />
            </div>
        </FormPage>
    )
}

function Certifications({ distributorLocation, currentUser, isEditMode }) {
    if (currentUser.isEmployee && isEditMode)
        return (
            <CertificationsEdit distributorLocation={distributorLocation} />
        )
    else
        return null
}

function CertificationsEdit({ distributorLocation }) {
    const stateCodes = ["AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WI", "WV", "WY",
        "ON", "QC", "NS", "NB", "MB", "BC", "PE", "SK", "AB", "NL", "NT", "YT", "NU"]
    const regionCodes = ["SA", "CA", "WE", "EE", "PR", "BR", "AF", "RU", "AU", "IN"]
    return (<div>
        <span>
            {serviceTable("State", distributorLocation, stateCodes)}
        </span>
        <span>
            {serviceTable("Region", distributorLocation, regionCodes)}
        </span>
    </div>)
}

function serviceTable(label, distributorLocation, areas) {
    return (
        <table>
                <tr>
                    <td>{label}</td>
                    <td>Gas</td>
                    <td>Industrial</td>
                    <td>Service</td>
                    <td>Rental</td>
                    <td>Certified Rental</td>
                    <td>PPR</td>
                </tr>
                <tbody>
                    {areas.map(function (code) {
                        const location = label.toLowerCase() + '_' + code
                        let service = null
                        if (distributorLocation?.services != null) {
                            for (const s of distributorLocation.services) {
                                if (s.location === location) {
                                    service = s
                                }
                            }
                        }
                        return (
                            <tr>
                                <td>{code}</td>
                                <td><input type="checkbox" name={location + '_gas'} defaultChecked={service?.gas} /></td>
                                <td><input type="checkbox" name={location + '_industrial'} defaultChecked={service?.industrial} /></td>
                                <td><input type="checkbox" name={location + '_service'} defaultChecked={service?.service} /></td>
                                <td><input type="checkbox" name={location + '_rental'} defaultChecked={service?.rental} /></td>
                                <td><input type="checkbox" name={location + '_certifiedRental'} defaultChecked={service?.certifiedRental} /></td>
                                <td><input type="checkbox" name={location + '_ppr'} defaultChecked={service?.ppr} /></td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
    )
}

/*
    Helper function to create form buttons
*/
const PLAIN = 0
const PRIMARY = 1
const CAUTION = 2
function createButton(buttons, add, label, level, fun) {
    if (add) {
        buttons.push({
            label: label,
            primary: level === PRIMARY ? true : false,
            caution: level === CAUTION ? true : false,
            onClick: fun
        })
    }
}
